import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import JSZip from "jszip";
import { useAuth } from "../../context/AuthContext";
import Card from "../../ui/Card";
import { buildSessionsCsvFiltered, clearOrgDemo, countOrgSessionsOlderThan, createDemoOrg, createOrg, exportOrgSessionsCsv, exportSessionsCsvFiltered, fetchOrgHealthSummaries, fetchOrgProfile, fetchOrgSettings, getDefaultOrgTheme, listOrgs, LIVE_NIA_ORG_NAME, LIVE_SHUTTLEWORTH_ORG_ID, prepareShuttleworthReserveOrg, pruneOrgSessions, saveOrgProfile, seedOrgDemo, seedOrgSessionsToFirestore, SHUTTLEWORTH_RESERVE_ORG_ID, updateOrgMeta } from "../../utils/orgData";
import { generateThemeFromBrandColors, getThemeModeTokens } from "../../theme";
import { buildActivityCsv, buildAuditCsv, exportActivityCsv, exportAuditCsv, fetchOrgActivity, fetchOrgAudit, fetchOrgAuditAll, logOrgActivity, pruneOrgAudit } from "../../utils/orgAudit";
import { addSuperadminToOrg, seedOrgUsers } from "../../utils/orgUsers";
import { syncLocalSessionsToFirestore } from "../../utils/sessionSync";
import { db, storage } from "../../firebase";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { addDoc, collection, doc, getDocs, limit, orderBy, query, serverTimestamp, updateDoc } from "firebase/firestore";

const LS_KEY = "org_profile"; // {name, logoUrl, theme}
const HEX_6_RE = /^#[0-9A-F]{6}$/;
const MAX_LOGO_BYTES = 100 * 1024;
const ACCEPTED_LOGO_TYPES = new Set(["image/png", "image/svg+xml"]);
const toHex6 = (value, fallback = "#000000") => {
  const input = String(value || "").trim().toUpperCase();
  if (HEX_6_RE.test(input)) return input;
  return fallback;
};
const formatBytes = (bytes = 0) => `${Math.ceil(bytes / 1024)} KB`;

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Unable to read file"));
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Unable to decode image"));
    img.src = src;
  });

const canvasToPngBlob = (canvas) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png");
  });

async function compressPngToLimit(file, maxBytes = MAX_LOGO_BYTES) {
  if (!file || file.type !== "image/png" || file.size <= maxBytes) return file;
  const src = await readFileAsDataUrl(file);
  const img = await loadImage(src);
  const ratios = [1, 0.9, 0.82, 0.74, 0.66, 0.58, 0.5, 0.42, 0.35, 0.28];

  for (const ratio of ratios) {
    const width = Math.max(1, Math.round(img.width * ratio));
    const height = Math.max(1, Math.round(img.height * ratio));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) continue;
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await canvasToPngBlob(canvas);
    if (blob && blob.size <= maxBytes) {
      return new File([blob], file.name.replace(/\.[^.]+$/, ".png"), {
        type: "image/png",
        lastModified: Date.now(),
      });
    }
  }
  return null;
}

export default function AdminPage() {
  const { user, activeOrgId, setActiveOrgId } = useAuth();
  const isReadOnly = user?.role === "investor";
  const [name, setName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoDarkUrl, setLogoDarkUrl] = useState("");
  const [lightLogoStatus, setLightLogoStatus] = useState("");
  const [darkLogoStatus, setDarkLogoStatus] = useState("");
  const [lightLogoFileName, setLightLogoFileName] = useState("");
  const [darkLogoFileName, setDarkLogoFileName] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState({ light: false, dark: false });
  const [brandColors, setBrandColors] = useState(() => ({ primary: "#000092", accent: "#0f6e73" }));
  const [theme, setTheme] = useState(() => getDefaultOrgTheme());
  const [themeTab, setThemeTab] = useState("light");
  const [hasCustomTheme, setHasCustomTheme] = useState(false);
  const [settings, setSettings] = useState({});
  const [orgType, setOrgType] = useState("pilot");
  const [orgs, setOrgs] = useState([]);
  const [orgIdInput, setOrgIdInput] = useState("");
  const [orgNameInput, setOrgNameInput] = useState("");
  const [auditRows, setAuditRows] = useState([]);
  const [auditFilter, setAuditFilter] = useState("");
  const [auditAllOrgs, setAuditAllOrgs] = useState(false);
  const [auditOrgId, setAuditOrgId] = useState("all");
  const [auditDays, setAuditDays] = useState("30");
  const [auditRetentionDays, setAuditRetentionDays] = useState("90");
  const [orgNotice, setOrgNotice] = useState("");
  const [orgError, setOrgError] = useState("");
  const [preparingReserveOrg, setPreparingReserveOrg] = useState(false);
  const [orgHealth, setOrgHealth] = useState([]);
  const [activityRows, setActivityRows] = useState([]);
  const [activityType, setActivityType] = useState("all");
  const [activityDays, setActivityDays] = useState("30");
  const [activitySearch, setActivitySearch] = useState("");
  const [orgSearch, setOrgSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [selectedOrgIds, setSelectedOrgIds] = useState(() => new Set());
  const [bulkTagInput, setBulkTagInput] = useState("");
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editLogo, setEditLogo] = useState("");
  const [editType, setEditType] = useState("pilot");
  const [editLogoError, setEditLogoError] = useState(false);
  const [sessionExportRole, setSessionExportRole] = useState("all");
  const [sessionExportDays, setSessionExportDays] = useState("30");
  const [sessionExportUser, setSessionExportUser] = useState("");
  const [sessionExportTag, setSessionExportTag] = useState("");
  const [sessionPruneDays, setSessionPruneDays] = useState("180");
  const [prunePreview, setPrunePreview] = useState(null);
  const [announceTitle, setAnnounceTitle] = useState("");
  const [announceDetail, setAnnounceDetail] = useState("");
  const [announceTag, setAnnounceTag] = useState("Release");
  const [announceActive, setAnnounceActive] = useState(true);
  const [editingAnnouncementId, setEditingAnnouncementId] = useState("");
  const [announceStatus, setAnnounceStatus] = useState("");
  const [publishingAnnouncement, setPublishingAnnouncement] = useState(false);
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);
  const [lastChange, setLastChange] = useState(null);
  const [bulkSeats, setBulkSeats] = useState("");
  const [quickTagPresets, setQuickTagPresets] = useState(() => {
    try {
      const stored = localStorage.getItem("nia_quick_tags");
      if (stored) return JSON.parse(stored);
    } catch {}
    return ["pilot", "enterprise", "trial", "at-risk", "high-growth"];
  });
  const [quickTagInput, setQuickTagInput] = useState("");
  const uniqueQuickTags = useMemo(
    () => Array.from(new Set(quickTagPresets.map((tag) => tag.trim()).filter(Boolean))),
    [quickTagPresets]
  );
  const [exportNotice, setExportNotice] = useState(null);
  const [themeHexDrafts, setThemeHexDrafts] = useState({});
  const [themeHexErrors, setThemeHexErrors] = useState({});
  const themeColorRefs = useRef({});
  const brandColorRefs = useRef({});

  useEffect(() => {
    let mounted = true;
    fetchOrgProfile(user, activeOrgId || "org_default")
      .then((profile) => {
        if (!mounted) return;
        setName(profile.name || "Your Organisation");
        setLogoUrl(profile.logoUrl || "");
        setLogoDarkUrl(profile.logoDarkUrl || "");
        setLightLogoStatus("");
        setDarkLogoStatus("");
        setLightLogoFileName("");
        setDarkLogoFileName("");
        setTheme(profile.theme || getDefaultOrgTheme());
        setBrandColors(profile.brandColors || { primary: profile.primary || "#000092", accent: profile.accent || "#0f6e73" });
        setHasCustomTheme(Boolean(profile.hasCustomTheme));
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [user, activeOrgId]);

  useEffect(() => {
    let mounted = true;
    fetchOrgSettings(user, activeOrgId || "org_default")
      .then((next) => {
        if (!mounted) return;
        const safe = next || {};
        setSettings(safe);
        setOrgType(String(safe.type || "pilot"));
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [user, activeOrgId]);

  useEffect(() => {
    if (user?.role !== "superadmin") return;
    listOrgs(user)
      .then((list) => setOrgs(list))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user?.role !== "superadmin") return;
    fetchOrgHealthSummaries(user)
      .then((rows) => setOrgHealth(rows))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user?.role !== "superadmin") return;
    if (auditAllOrgs) {
      fetchOrgAuditAll(user, 50)
        .then((rows) => setAuditRows(rows))
        .catch(() => {});
      return;
    }
    fetchOrgAudit(user, activeOrgId || "org_default", 15)
      .then((rows) => setAuditRows(rows))
      .catch(() => {});
  }, [user, activeOrgId, auditAllOrgs]);

  useEffect(() => {
    if (user?.role !== "superadmin") return;
    if (!activeOrgId) return;
    pruneOrgAudit(user, activeOrgId, auditRetentionDays);
  }, [user, activeOrgId, auditRetentionDays]);

  useEffect(() => {
    if (!user?.uid) return;
    fetchOrgActivity(user, activeOrgId || "org_default", 12)
      .then((rows) => setActivityRows(rows))
      .catch(() => {});
  }, [user, activeOrgId]);

  const canManageAnnouncements = user?.role === "admin" || user?.role === "superadmin";

  const resetAnnouncementForm = useCallback(() => {
    setEditingAnnouncementId("");
    setAnnounceTitle("");
    setAnnounceDetail("");
    setAnnounceTag("Release");
    setAnnounceActive(true);
  }, []);

  const loadRecentAnnouncements = useCallback(async () => {
    if (!canManageAnnouncements) return;
    try {
      const q = query(collection(db, "announcements"), orderBy("createdAt", "desc"), limit(8));
      const snap = await getDocs(q);
      const rows = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      setRecentAnnouncements(rows);
    } catch {
      setRecentAnnouncements([]);
    }
  }, [canManageAnnouncements]);

  useEffect(() => {
    if (!canManageAnnouncements) return;
    loadRecentAnnouncements();
  }, [canManageAnnouncements, loadRecentAnnouncements]);

  const beginAnnouncementEdit = useCallback((item) => {
    if (!item?.id) return;
    setEditingAnnouncementId(String(item.id));
    setAnnounceTitle(String(item.title || ""));
    setAnnounceDetail(String(item.detail || ""));
    setAnnounceTag(String(item.tag || "Release"));
    setAnnounceActive(Boolean(item.active));
    setAnnounceStatus("Editing existing announcement.");
  }, []);

  useEffect(() => {
    if (!auditAllOrgs) {
      const next = activeOrgId || "org_default";
      if (auditOrgId !== next) setAuditOrgId(next);
    } else if (auditOrgId === "") {
      setAuditOrgId("all");
    }
  }, [auditAllOrgs, activeOrgId, auditOrgId]);

  const save = () => {
    const orgId = activeOrgId || "org_default";
    saveOrgProfile(user, orgId, { name, logoUrl, logoDarkUrl, brandColors, theme, hasCustomTheme });
    updateOrgMeta(user, orgId, { type: orgType });
    try {
      const cached = { name, logoUrl, logoDarkUrl, primary: brandColors.primary, accent: brandColors.accent };
      if (hasCustomTheme) cached.theme = theme;
      localStorage.setItem(`${LS_KEY}__${orgId}`, JSON.stringify(cached));
    } catch {}
    alert("Saved.");
  };

  const exportSessions = () => {
    const orgId = activeOrgId || "org_default";
    const key = `nia_sessions__org_${orgId}`;
    const global = localStorage.getItem(key) || "[]";
    const blob = new Blob([global], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "nia-sessions.json";
    a.click();
  };

  const hasName = Boolean(name && name.trim() && name !== "Your Organisation");
  const hasSeats = Number(settings?.planSeats) > 0;
  const hasPrice = Number(settings?.pricePerSeat) > 0;
  const needsSetup = !(hasName && hasSeats && hasPrice);
  const setupDone = !needsSetup;

  const sanitizeOrgId = (value) =>
    value
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-_]/g, "");

  const isValidUrl = (value) => {
    if (!value) return true;
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const updateBrandColor = (key, value) => {
    setBrandColors((prev) => {
      const next = { ...(prev || {}), [key]: value };
      if (!hasCustomTheme) {
        setTheme(generateThemeFromBrandColors(next.primary, next.accent));
      }
      return next;
    });
  };

  const saveAsCustomTheme = () => {
    setHasCustomTheme(true);
    setTheme((prev) => prev || generateThemeFromBrandColors(brandColors.primary, brandColors.accent));
  };

  const resetToAutoTheme = () => {
    setHasCustomTheme(false);
    setTheme(generateThemeFromBrandColors(brandColors.primary, brandColors.accent));
  };

  const uploadLogoFile = async (variant, file) => {
    const isDarkVariant = variant === "dark";
    const setStatus = isDarkVariant ? setDarkLogoStatus : setLightLogoStatus;
    const setFileName = isDarkVariant ? setDarkLogoFileName : setLightLogoFileName;
    if (!file) return;
    setStatus("");
    setFileName(file.name);

    if (!ACCEPTED_LOGO_TYPES.has(file.type)) {
      setStatus("Only PNG and SVG files are supported.");
      return;
    }

    let uploadFile = file;
    if (file.type === "image/svg+xml" && file.size > MAX_LOGO_BYTES) {
      setStatus(`SVG is ${formatBytes(file.size)}. Max size is ${formatBytes(MAX_LOGO_BYTES)}.`);
      return;
    }
    if (file.type === "image/png" && file.size > MAX_LOGO_BYTES) {
      setStatus(`PNG is ${formatBytes(file.size)}. Optimizing…`);
      try {
        const compressed = await compressPngToLimit(file, MAX_LOGO_BYTES);
        if (!compressed) {
          setStatus(`Could not reduce PNG below ${formatBytes(MAX_LOGO_BYTES)}. Please upload a smaller file.`);
          return;
        }
        uploadFile = compressed;
      } catch {
        setStatus("PNG optimization failed. Please upload a smaller file.");
        return;
      }
    }
    if (uploadFile.size > MAX_LOGO_BYTES) {
      setStatus(`File is ${formatBytes(uploadFile.size)}. Max size is ${formatBytes(MAX_LOGO_BYTES)}.`);
      return;
    }

    const orgId = activeOrgId || "org_default";
    const safeName = file.name.toLowerCase().replace(/[^a-z0-9._-]+/g, "-");
    const path = `orgs/${orgId}/branding/${variant}-logo-${Date.now()}-${safeName}`;

    setUploadingLogo((prev) => ({ ...prev, [variant]: true }));
    setStatus("Uploading…");
    try {
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, uploadFile, { contentType: uploadFile.type });
      const url = await getDownloadURL(storageRef);
      if (isDarkVariant) {
        setLogoDarkUrl(url);
      } else {
        setLogoUrl(url);
      }
      setStatus(`Uploaded (${formatBytes(uploadFile.size)}).`);
    } catch (err) {
      const detail = String(err?.code || err?.message || "").toLowerCase();
      if (detail.includes("cors") || detail.includes("preflight")) {
        setStatus("Upload blocked by bucket CORS policy. Apply Firebase Storage CORS config (see README).");
      } else {
        setStatus("Upload failed. Check Firebase Storage bucket config, CORS, and permissions.");
      }
    } finally {
      setUploadingLogo((prev) => ({ ...prev, [variant]: false }));
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setOrgNotice("Copied to clipboard");
    } catch {
      setOrgError("Unable to copy to clipboard.");
    }
  };

  const downloadBlob = (blob, name) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
  };

  const downloadCsv = (csv, name) => {
    const blob = new Blob([csv], { type: "text/csv" });
    downloadBlob(blob, name);
  };

  const buildOrgListCsv = (list = orgs) => {
    const header = ["Org ID", "Name", "Archived", "Tags", "Seats Used", "Seats Total"];
    const lines = list.map((org) => [
      org.id,
      org.name || "",
      org.archived ? "yes" : "no",
      (org.tags || []).join("|"),
      org.seatsUsed || "",
      org.seatsTotal || "",
    ]);
    return [header, ...lines].map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  };

  const exportAdminPack = async () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const zip = new JSZip();
    if (orgs.length > 0) {
      zip.file(`nia-orgs-${stamp}.csv`, buildOrgListCsv(orgs));
    }
    zip.file(`nia-activity-${activeOrgId || "org"}-${stamp}-filtered.csv`, buildActivityCsv(filteredActivity));
    zip.file(`nia-audit-${auditOrgId || "org"}-${stamp}-filtered.csv`, buildAuditCsv(filteredAudit));
    const sessionsCsv = await buildSessionsCsvFiltered(
      user,
      activeOrgId || "org_default",
      { role: sessionExportRole, days: sessionExportDays, userSearch: sessionExportUser, tag: sessionExportTag }
    );
    zip.file(`nia-sessions-${activeOrgId || "org_default"}-${stamp}.csv`, sessionsCsv);
    const blob = await zip.generateAsync({ type: "blob" });
    downloadBlob(blob, `nia-admin-pack-${stamp}.zip`);
  };

  const exportOrgPack = async (orgId) => {
    const stamp = new Date().toISOString().slice(0, 10);
    const org = orgs.find((o) => o.id === orgId);
    const name = org?.name ? org.name.toLowerCase().replace(/\s+/g, "-") : orgId;
    const zip = new JSZip();
    const sessionsCsv = await buildSessionsCsvFiltered(
      user,
      orgId,
      { role: "all", days: "0", userSearch: "" }
    );
    zip.file(`nia-sessions-${name}-${stamp}.csv`, sessionsCsv);
    const activity = await fetchOrgActivity(user, orgId, 200);
    const audit = await fetchOrgAudit(user, orgId, 200);
    if (activity.length) {
      zip.file(`nia-activity-${name}-${stamp}.csv`, buildActivityCsv(activity));
    }
    if (audit.length) {
      zip.file(`nia-audit-${name}-${stamp}.csv`, buildAuditCsv(audit));
    }
    const blob = await zip.generateAsync({ type: "blob" });
    downloadBlob(blob, `nia-org-pack-${name}-${stamp}.zip`);
  };

  const uniqueTags = Array.from(
    new Set(orgs.flatMap((org) => org.tags || []))
  );

  const themeStatus = hasCustomTheme ? "Custom" : "Auto (generated)";
  const visibleTheme = getThemeModeTokens(theme || getDefaultOrgTheme(), themeTab);
  const tokenPreview = useMemo(
    () => [
      { id: "primary", label: "Primary", group: "color", key: "primary", value: visibleTheme.color.primary },
      { id: "accent", label: "Accent", group: "color", key: "accent", value: visibleTheme.color.accent },
      { id: "background", label: "Background", group: "color", key: "background", value: visibleTheme.color.background },
      { id: "surface", label: "Surface", group: "color", key: "surface", value: visibleTheme.color.surface },
      { id: "surfaceAlt", label: "Surface Alt", group: "color", key: "surfaceAlt", value: visibleTheme.color.surfaceAlt },
      { id: "text", label: "Text", group: "color", key: "text", value: visibleTheme.color.text },
      { id: "textMuted", label: "Muted", group: "color", key: "textMuted", value: visibleTheme.color.textMuted },
      { id: "border", label: "Border", group: "color", key: "border", value: visibleTheme.color.border },
      { id: "focusRing", label: "Focus Ring", group: "color", key: "focusRing", value: visibleTheme.color.focusRing },
      { id: "chart1", label: "Chart 1", group: "chart", key: 1, value: visibleTheme.chart[1] },
      { id: "chart2", label: "Chart 2", group: "chart", key: 2, value: visibleTheme.chart[2] },
      { id: "chart3", label: "Chart 3", group: "chart", key: 3, value: visibleTheme.chart[3] },
      { id: "chart4", label: "Chart 4", group: "chart", key: 4, value: visibleTheme.chart[4] },
      { id: "chart5", label: "Chart 5", group: "chart", key: 5, value: visibleTheme.chart[5] },
      { id: "chart6", label: "Chart 6", group: "chart", key: 6, value: visibleTheme.chart[6] },
    ],
    [visibleTheme]
  );

  useEffect(() => {
    setThemeHexDrafts((prev) => {
      let changed = false;
      const next = { ...prev };
      tokenPreview.forEach((field) => {
        const tokenId = `${themeTab}:${field.id}`;
        const normalized = toHex6(field.value, "#000000");
        if (next[tokenId] !== normalized) {
          next[tokenId] = normalized;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setThemeHexErrors((prev) => {
      let changed = false;
      const next = { ...prev };
      tokenPreview.forEach((field) => {
        const tokenId = `${themeTab}:${field.id}`;
        if (next[tokenId] !== false) {
          next[tokenId] = false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [themeTab, tokenPreview]);

  const updateCustomThemeField = (field, value) => {
    const normalized = toHex6(value, toHex6(field.value, "#000000")).toLowerCase();
    const baseTheme = theme || generateThemeFromBrandColors(brandColors.primary, brandColors.accent);
    const modeTheme = baseTheme?.[themeTab] || getThemeModeTokens(baseTheme, themeTab);
    const nextModeTheme = {
      ...modeTheme,
      color: { ...(modeTheme?.color || {}) },
      chart: { ...(modeTheme?.chart || {}) },
    };
    if (field.group === "chart") {
      nextModeTheme.chart[field.key] = normalized;
    } else {
      nextModeTheme.color[field.key] = normalized;
    }
    setTheme({
      ...(baseTheme || {}),
      [themeTab]: nextModeTheme,
    });
    setHasCustomTheme(true);
  };

  const filteredOrgs = orgs
    .filter((org) => (showArchived ? true : !org.archived))
    .filter((org) => {
      if (!orgSearch.trim()) return true;
      const needle = orgSearch.toLowerCase();
      return `${org.name} ${org.id}`.toLowerCase().includes(needle);
    })
    .filter((org) => {
      if (tagFilter === "all") return true;
      return org.tags?.includes(tagFilter);
    });

  const toggleOrgSelection = (orgId) => {
    setSelectedOrgIds((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  };

  const clearOrgSelection = () => setSelectedOrgIds(new Set());

  useEffect(() => {
    try {
      localStorage.setItem("nia_quick_tags", JSON.stringify(quickTagPresets));
    } catch {}
  }, [quickTagPresets]);

  const addQuickTag = () => {
    const tag = quickTagInput.trim();
    if (!tag) return;
    setQuickTagPresets((prev) => {
      if (prev.includes(tag)) return prev;
      return [...prev, tag];
    });
    setQuickTagInput("");
  };

  const removeQuickTag = (tag) => {
    setQuickTagPresets((prev) => prev.filter((t) => t !== tag));
  };

  const confirmExport = (title, detail, action) => {
    setExportNotice({ title, detail, action });
  };

  const applyBulkTags = async (mode) => {
    if (!bulkTagInput.trim()) {
      setOrgError("Enter at least one tag.");
      return;
    }
    if (!selectedOrgIds.size) {
      setOrgError("Select one or more orgs first.");
      return;
    }
    const tags = bulkTagInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (!tags.length) {
      setOrgError("Enter at least one tag.");
      return;
    }
    const ids = Array.from(selectedOrgIds);
    await Promise.all(ids.map(async (id) => {
      const org = orgs.find((o) => o.id === id);
      if (!org) return;
      const current = org.tags || [];
      const nextTags = mode === "replace"
        ? tags
        : Array.from(new Set([...current, ...tags]));
      await updateOrgMeta(user, id, { tags: nextTags });
      logOrgActivity(user, id, "org_tags_update", {
        before: { tags: current },
        after: { tags: nextTags },
      });
    }));
    setOrgs((prev) => prev.map((org) => {
      if (!selectedOrgIds.has(org.id)) return org;
      const current = org.tags || [];
      const nextTags = mode === "replace"
        ? tags
        : Array.from(new Set([...current, ...tags]));
      return { ...org, tags: nextTags };
    }));
    setOrgNotice(`Tags ${mode === "replace" ? "replaced" : "added"} for ${selectedOrgIds.size} orgs`);
    setBulkTagInput("");
    clearOrgSelection();
  };

  const setUndo = (label, undoFn) => {
    setLastChange({ label, undoFn });
  };

  const applyOrgUpdate = (orgId, updates) => {
    setOrgs((prev) => prev.map((org) => (org.id === orgId ? { ...org, ...updates } : org)));
  };

  const refreshOrgRegistry = useCallback(async () => {
    if (user?.role !== "superadmin") return;
    try {
      const list = await listOrgs(user);
      setOrgs(list);
    } catch {}
  }, [user]);

  const handlePrepareShuttleworthReserve = useCallback(async () => {
    if (user?.role !== "superadmin" || isReadOnly || preparingReserveOrg) return;
    const ok = window.confirm(
      `Create archived reserve org ${SHUTTLEWORTH_RESERVE_ORG_ID} from ${LIVE_SHUTTLEWORTH_ORG_ID} and rebrand the live org to ${LIVE_NIA_ORG_NAME}? Existing users, sessions, memberships, and roles will stay on ${LIVE_SHUTTLEWORTH_ORG_ID}.`
    );
    if (!ok) return;
    setPreparingReserveOrg(true);
    setOrgError("");
    setOrgNotice("");
    try {
      const priorLiveOrg = orgs.find((org) => org.id === LIVE_SHUTTLEWORTH_ORG_ID);
      const result = await prepareShuttleworthReserveOrg(user);
      if (!result?.ok) {
        const reasonMap = {
          forbidden: "Only superadmin can prepare the Shuttleworth reserve org.",
          invalid_org_id: "Reserve-org setup is missing a required org ID.",
          source_missing: `Live source org ${LIVE_SHUTTLEWORTH_ORG_ID} was not found.`,
          reserve_exists: `Reserve org ${SHUTTLEWORTH_RESERVE_ORG_ID} already exists. No changes were made.`,
          write_failed: "Unable to write the reserve/rebrand update. Check Firestore access and try again.",
        };
        setOrgError(reasonMap[result?.reason] || "Unable to prepare the Shuttleworth reserve org.");
        return;
      }
      await refreshOrgRegistry();
      setShowArchived(true);
      setActiveOrgId(result.sourceOrgId);
      const [profile, nextSettings] = await Promise.all([
        fetchOrgProfile(user, result.sourceOrgId),
        fetchOrgSettings(user, result.sourceOrgId),
      ]);
      setName(profile?.name || result.liveName);
      setLogoUrl(profile?.logoUrl || "");
      setLogoDarkUrl(profile?.logoDarkUrl || "");
      setTheme(profile?.theme || getDefaultOrgTheme());
      setBrandColors(profile?.brandColors || { primary: profile?.primary || "#000092", accent: profile?.accent || "#0f6e73" });
      setHasCustomTheme(Boolean(profile?.hasCustomTheme));
      setSettings(nextSettings || {});
      setOrgType(String(nextSettings?.type || "pilot"));
      logOrgActivity(user, result.sourceOrgId, "org_rebrand_live", {
        before: { name: priorLiveOrg?.name || LIVE_SHUTTLEWORTH_ORG_ID },
        after: { name: result.liveName },
      });
      logOrgActivity(user, result.reserveOrgId, "org_create_reserve", {
        after: {
          archived: true,
          reserveCopyOf: result.sourceOrgId,
          name: result.reserveName,
        },
      });
      setOrgNotice(`Live org ${result.sourceOrgId} is now ${result.liveName}. Archived reserve copy created as ${result.reserveOrgId}.`);
    } finally {
      setPreparingReserveOrg(false);
    }
  }, [isReadOnly, orgs, preparingReserveOrg, refreshOrgRegistry, setActiveOrgId, user]);

  const filteredAudit = auditRows.filter((row) => {
    if (auditOrgId !== "all" && row.orgId !== auditOrgId) return false;
    if (auditFilter.trim()) {
      const needle = auditFilter.toLowerCase();
      const actor = (row.actorEmail || row.actorId || "").toLowerCase();
      if (!actor.includes(needle)) return false;
    }
    if (auditDays) {
      const days = Number(auditDays);
      if (Number.isFinite(days) && days > 0) {
        const ts = row.createdAt?.toDate?.() || null;
        if (ts) {
          const cutoff = Date.now() - days * 24 * 3600 * 1000;
          if (ts.getTime() < cutoff) return false;
        }
      }
    }
    return true;
  });

  const filteredActivity = activityRows.filter((row) => {
    if (activityType !== "all" && row.type !== activityType) return false;
    if (activitySearch.trim()) {
      const needle = activitySearch.toLowerCase();
      const actor = (row.actorEmail || row.actorId || "").toLowerCase();
      if (!actor.includes(needle)) return false;
    }
    const days = Number(activityDays);
    if (Number.isFinite(days) && days > 0) {
      const ts = row.createdAt?.toDate?.() || null;
      if (ts) {
        const cutoff = Date.now() - days * 24 * 3600 * 1000;
        if (ts.getTime() < cutoff) return false;
      }
    }
    return true;
  });
  const activityFeed = filteredActivity.slice(0, 6);

  const activityDetail = (row) => {
    if (row.type === "session_created") {
      return row.sessionId ? `Session ID: ${row.sessionId}` : "Session created";
    }
    const after = row.after || {};
    const keys = Object.keys(after);
    if (keys.length) {
      return `Updated ${keys.join(", ")}`;
    }
    return row.detail || "";
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 org-admin-page">
      {needsSetup && (
        <Card>
          <h3 className="text-lg font-semibold">Org Setup Wizard</h3>
          <p className="text-sm opacity-70 mt-2">
            Finish these basics to make your org ready for demos.
          </p>
          <div className="mt-3 grid gap-2">
            <div className="nia-list-btn">
              <span>Set organisation name + logo</span>
              <span className="nia-pill">Step 1</span>
            </div>
            <div className="nia-list-btn">
              <span>Configure seats + billing plan</span>
              <span className="nia-pill">Step 2</span>
            </div>
            <div className="nia-list-btn">
              <span>Seed demo data for insights</span>
              <span className="nia-pill">Step 3</span>
            </div>
          </div>
        </Card>
      )}
      {setupDone && (
        <Card>
          <h3 className="text-lg font-semibold">Org Setup Complete</h3>
          <p className="text-sm opacity-70 mt-2">
            Your org profile and billing baseline are configured. You are ready to demo.
          </p>
        </Card>
      )}

      {lastChange && (
        <Card className="nia-undo-banner">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <strong>Change saved:</strong> {lastChange.label}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn btn-ghost"
                onClick={async () => {
                  await lastChange.undoFn?.();
                  setLastChange(null);
                }}
              >
                Undo
              </button>
              <button className="btn btn-ghost" onClick={() => setLastChange(null)}>Dismiss</button>
            </div>
          </div>
        </Card>
      )}

      {exportNotice && (
        <div style={{ position: "fixed", inset: 0, background: "var(--color-overlay)", zIndex: 999 }}>
          <div style={{ maxWidth: 520, margin: "10% auto", background: "var(--ui-surface)", padding: 20, borderRadius: 12, border: "1px solid var(--ui-border)" }}>
            <h3 style={{ marginTop: 0 }}>{exportNotice.title}</h3>
            <p className="nia-muted">{exportNotice.detail}</p>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button
                className="btn btn-primary"
                onClick={async () => {
                  await exportNotice.action?.();
                  setExportNotice(null);
                }}
              >
                Download
              </button>
              <button className="btn btn-ghost" onClick={() => setExportNotice(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {user?.role === "superadmin" && (
        <Card>
          <h3 className="text-lg font-semibold">Org Health Overview</h3>
          <p className="text-sm opacity-70 mt-2">
            Snapshot across all organisations.
          </p>
          <div className="mt-3 grid gap-2">
            <div className="nia-list-btn">
              <span>Total orgs</span>
              <span className="nia-pill">{orgHealth.length}</span>
            </div>
            <div className="nia-list-btn">
              <span>Total seats used</span>
              <span className="nia-pill">
                {orgHealth.reduce((sum, o) => sum + (o.seatsUsed || 0), 0)}
              </span>
            </div>
            <div className="nia-list-btn">
              <span>Active users (7d)</span>
              <span className="nia-pill">
                {orgHealth.reduce((sum, o) => sum + (o.activeUsersWeek || 0), 0)}
              </span>
            </div>
            <div className="nia-list-btn">
              <span>Sessions (30d)</span>
              <span className="nia-pill">
                {orgHealth.reduce((sum, o) => sum + (o.sessions30 || 0), 0)}
              </span>
            </div>
            <div className="nia-list-btn">
              <span>Most recent activity</span>
              <span className="nia-pill">
                {(() => {
                  const latest = orgHealth
                    .map((o) => o.lastActivity || 0)
                    .filter(Boolean)
                    .sort((a, b) => b - a)[0];
                  return latest ? new Date(latest).toLocaleString() : "—";
                })()}
              </span>
            </div>
          </div>
          {orgHealth.length > 0 && (
            <div className="mt-3">
              <div className="text-sm opacity-70">Quick switch</div>
              <div className="nia-list" style={{ marginTop: 8 }}>
                {orgHealth.map((org) => (
                  <div key={org.id} className="nia-list-btn" style={{ justifyContent: "space-between" }}>
                    <button
                      className="btn btn-ghost"
                      onClick={() => setActiveOrgId(org.id)}
                      style={{ padding: 0 }}
                    >
                      {org.name}
                    </button>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span className="nia-pill">
                        {org.seatsUsed || 0}/{org.seatsTotal || 0}
                      </span>
                      <button className="btn btn-ghost" onClick={() => copyText(org.id)}>
                        Copy ID
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <Card>
        <h3 className="text-lg font-semibold">Organisation Profile</h3>
        <div className="grid gap-2 mt-3">
          <label className="label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} disabled={isReadOnly} />

          <label className="label">Logo URL</label>
          <input className="input" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} disabled={isReadOnly} />
          <label className="label">Upload Light/Default Logo (PNG or SVG, max 100 KB)</label>
          <input
            className="input"
            type="file"
            accept=".png,.svg,image/png,image/svg+xml"
            disabled={isReadOnly || uploadingLogo.light}
            onChange={(e) => {
              const file = e.target.files?.[0];
              uploadLogoFile("light", file);
              e.target.value = "";
            }}
          />
          {(lightLogoFileName || lightLogoStatus) && (
            <div className="nia-muted" style={{ fontSize: 12 }}>
              {lightLogoFileName ? `${lightLogoFileName} • ` : ""}{lightLogoStatus}
            </div>
          )}

          <label className="label">Dark Mode Logo URL</label>
          <input className="input" value={logoDarkUrl} onChange={(e) => setLogoDarkUrl(e.target.value)} disabled={isReadOnly} />
          <label className="label">Upload Dark Mode Logo (PNG or SVG, max 100 KB)</label>
          <input
            className="input"
            type="file"
            accept=".png,.svg,image/png,image/svg+xml"
            disabled={isReadOnly || uploadingLogo.dark}
            onChange={(e) => {
              const file = e.target.files?.[0];
              uploadLogoFile("dark", file);
              e.target.value = "";
            }}
          />
          {(darkLogoFileName || darkLogoStatus) && (
            <div className="nia-muted" style={{ fontSize: 12 }}>
              {darkLogoFileName ? `${darkLogoFileName} • ` : ""}{darkLogoStatus}
            </div>
          )}

          <div className="org-logo-preview-grid">
            <div className="org-logo-preview-item">
              <div className="nia-muted">Light / Default preview</div>
              {logoUrl ? (
                <img src={logoUrl} alt="Org light logo" style={{ height: 48, marginTop: 8, objectFit: "contain" }} />
              ) : (
                <div className="nia-muted" style={{ marginTop: 8 }}>No light logo set.</div>
              )}
            </div>
            <div className="org-logo-preview-item">
              <div className="nia-muted">Dark mode preview</div>
              {logoDarkUrl ? (
                <img src={logoDarkUrl} alt="Org dark logo" style={{ height: 48, marginTop: 8, objectFit: "contain" }} />
              ) : (
                <div className="nia-muted" style={{ marginTop: 8 }}>
                  No dark logo set. Dark mode will fall back to the main logo.
                </div>
              )}
            </div>
          </div>

          <label className="label">Org type</label>
          <select className="input" value={orgType} onChange={(e) => setOrgType(e.target.value)} disabled={isReadOnly}>
            <option value="live">Live</option>
            <option value="pilot">Pilot</option>
            <option value="demo">Demo</option>
          </select>

          <div className="mt-3">
            <div className="text-sm font-semibold">Brand Theme</div>
            <div className="text-xs opacity-70">
              These colors drive the org UI, charts, and accents. Use the org website palette.
            </div>
          </div>
          <div className="org-theme-grid" style={{ marginTop: 12 }}>
            {[
              { key: "primary", label: "Brand Primary" },
              { key: "accent", label: "Brand Accent" },
            ].map((field) => (
              <label key={field.key} className="org-theme-item">
                <span>{field.label}</span>
                <div className="org-theme-inputs">
                  <button
                    type="button"
                    className="org-theme-swatch"
                    onClick={() => brandColorRefs.current[field.key]?.click()}
                    style={{ "--swatch-color": toHex6(brandColors?.[field.key] || "#000000", "#000000") }}
                    aria-label={`Pick ${field.label} colour`}
                    disabled={isReadOnly}
                  />
                  <input
                    ref={(node) => { brandColorRefs.current[field.key] = node; }}
                    type="color"
                    className="org-theme-color-picker"
                    value={toHex6(brandColors?.[field.key] || "#000000", "#000000")}
                    onChange={(e) => updateBrandColor(field.key, e.target.value)}
                    disabled={isReadOnly}
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <input
                    className="input org-theme-hex-input"
                    value={brandColors?.[field.key] || ""}
                    onChange={(e) => updateBrandColor(field.key, e.target.value)}
                    disabled={isReadOnly}
                    placeholder="#RRGGBB"
                  />
                </div>
              </label>
            ))}
          </div>
          <div className="mt-3 flex gap-2" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <span className="nia-muted">Theme mode: {themeStatus}</span>
            {!hasCustomTheme && (
              <button type="button" className="btn btn-ghost" onClick={saveAsCustomTheme} disabled={isReadOnly}>
                Save as custom
              </button>
            )}
            {hasCustomTheme && (
              <button type="button" className="btn btn-ghost" onClick={resetToAutoTheme} disabled={isReadOnly}>
                Reset to auto
              </button>
            )}
          </div>
          <div className="mt-3 flex gap-2">
            {["light", "dark"].map((mode) => (
              <button
                key={mode}
                type="button"
                className={`btn ${themeTab === mode ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setThemeTab(mode)}
              >
                {mode === "light" ? "Light" : "Dark"}
              </button>
            ))}
          </div>
          <div className="org-theme-grid">
            {tokenPreview.map((field) => (
              <label key={field.label} className="org-theme-item">
                <span>{field.label}</span>
                <div className="org-theme-inputs">
                  <button
                    type="button"
                    className="org-theme-swatch"
                    onClick={() => themeColorRefs.current[`${themeTab}:${field.id}`]?.click()}
                    style={{ "--swatch-color": toHex6(themeHexDrafts[`${themeTab}:${field.id}`] || field.value, "#000000") }}
                    aria-label={`Pick ${field.label} colour`}
                    disabled={isReadOnly}
                  />
                  <input
                    ref={(node) => { themeColorRefs.current[`${themeTab}:${field.id}`] = node; }}
                    type="color"
                    className="org-theme-color-picker"
                    value={toHex6(themeHexDrafts[`${themeTab}:${field.id}`] || field.value, "#000000")}
                    onChange={(e) => {
                      const tokenId = `${themeTab}:${field.id}`;
                      const next = toHex6(e.target.value, "#000000");
                      setThemeHexDrafts((prev) => ({ ...prev, [tokenId]: next }));
                      setThemeHexErrors((prev) => ({ ...prev, [tokenId]: false }));
                      updateCustomThemeField(field, next);
                    }}
                    disabled={isReadOnly}
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <input
                    className={`input org-theme-hex-input${themeHexErrors[`${themeTab}:${field.id}`] ? " is-invalid" : ""}`}
                    value={themeHexDrafts[`${themeTab}:${field.id}`] || toHex6(field.value, "#000000")}
                    onChange={(e) => {
                      const tokenId = `${themeTab}:${field.id}`;
                      const next = String(e.target.value || "").toUpperCase();
                      setThemeHexDrafts((prev) => ({ ...prev, [tokenId]: next }));
                      if (HEX_6_RE.test(next)) {
                        setThemeHexErrors((prev) => ({ ...prev, [tokenId]: false }));
                        updateCustomThemeField(field, next);
                      } else {
                        setThemeHexErrors((prev) => ({ ...prev, [tokenId]: next.length > 0 && next.length >= 7 }));
                      }
                    }}
                    onBlur={() => {
                      const tokenId = `${themeTab}:${field.id}`;
                      const typed = themeHexDrafts[tokenId];
                      if (!HEX_6_RE.test(typed || "")) {
                        const fallback = toHex6(field.value, "#000000");
                        setThemeHexDrafts((prev) => ({ ...prev, [tokenId]: fallback }));
                        setThemeHexErrors((prev) => ({ ...prev, [tokenId]: false }));
                      }
                    }}
                    disabled={isReadOnly}
                    placeholder="#RRGGBB"
                  />
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button className="btn btn-primary" onClick={save} disabled={isReadOnly}>Save</button>
        </div>
      </Card>

      {user?.role === "superadmin" && (
        <Card>
          <h3 className="text-lg font-semibold">Org Registry</h3>
          <div className="grid gap-2 mt-3">
            <label className="label">Org ID</label>
            <input
              className="input"
              value={orgIdInput}
              onChange={(e) => setOrgIdInput(sanitizeOrgId(e.target.value))}
              disabled={isReadOnly}
            />
            {orgIdInput && orgIdInput.length < 3 && (
              <div className="text-sm" style={{ color: "var(--color-warning)" }}>
                Org ID should be at least 3 characters.
              </div>
            )}
            <label className="label">Org Name</label>
            <input className="input" value={orgNameInput} onChange={(e) => setOrgNameInput(e.target.value)} disabled={isReadOnly} />
            <button
              className="btn btn-primary"
              onClick={async () => {
                const cleanId = sanitizeOrgId(orgIdInput);
                if (!cleanId || cleanId.length < 3) {
                  setOrgError("Please enter a valid Org ID (min 3 chars).");
                  return;
                }
                await createOrg(user, cleanId, orgNameInput.trim());
                await addSuperadminToOrg(user, cleanId);
                const list = await listOrgs(user);
                setOrgs(list);
                setOrgNotice(`Org created: ${cleanId}`);
                setOrgError("");
                setOrgIdInput("");
                setOrgNameInput("");
              }}
              disabled={isReadOnly || !orgIdInput.trim()}
            >
              Create Org
            </button>
            <button
              className="btn btn-ghost"
              onClick={async () => {
                const id = activeOrgId || "org_default";
                try {
                  await navigator.clipboard.writeText(id);
                  setOrgNotice("Active org ID copied.");
                } catch {
                  setOrgNotice("Unable to copy. Please copy manually.");
                }
              }}
              disabled={isReadOnly}
            >
              Copy Active Org ID
            </button>
            <div className="nia-list-btn" style={{ cursor: "default", alignItems: "flex-start", flexDirection: "column" }}>
              <strong>Live org safeguard</strong>
              <span className="text-sm opacity-70">
                One-time action: keep <code>{LIVE_SHUTTLEWORTH_ORG_ID}</code> live for existing users, clone it to archived <code>{SHUTTLEWORTH_RESERVE_ORG_ID}</code>, and rebrand the live org to <code>{LIVE_NIA_ORG_NAME}</code>.
              </span>
              <button
                className="btn btn-ghost"
                onClick={handlePrepareShuttleworthReserve}
                disabled={isReadOnly || preparingReserveOrg}
              >
                {preparingReserveOrg ? "Preparing..." : "Prepare Shuttleworth reserve + Nia COACH rebrand"}
              </button>
            </div>
            <button
              className="btn btn-ghost"
              onClick={async () => {
                const id = await createDemoOrg(user);
                if (id) {
                  await seedOrgSessionsToFirestore(user, id);
                  await seedOrgUsers(user, id);
                  const list = await listOrgs(user);
                  setOrgs(list);
                  setOrgNotice(`Demo org created: ${id}`);
                }
              }}
              disabled={isReadOnly}
            >
              Create Demo Org
            </button>
            {orgs.length > 0 && (
              <button
                className="btn btn-ghost"
                onClick={async () => {
                  await Promise.all(orgs.map((org) => addSuperadminToOrg(user, org.id)));
                  setOrgNotice("Superadmin added to all org user lists.");
                }}
                disabled={isReadOnly}
              >
                Add superadmin to all orgs
              </button>
            )}
            {orgs.length > 0 && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  const stamp = new Date().toISOString().slice(0, 10);
                  downloadCsv(buildOrgListCsv(orgs), `nia-orgs-${stamp}.csv`);
                }}
                disabled={isReadOnly}
              >
                Export org list CSV
              </button>
            )}
            {orgs.length > 0 && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  const stamp = new Date().toISOString().slice(0, 10);
                  downloadCsv(buildOrgListCsv(filteredOrgs), `nia-orgs-filtered-${stamp}.csv`);
                }}
                disabled={isReadOnly}
              >
                Export filtered orgs CSV
              </button>
            )}
            <button
              className="btn btn-ghost"
              onClick={async () => {
                confirmExport(
                  "Download admin export pack",
                  "This will download a ZIP containing multiple CSV files (org list, activity, audit, sessions) based on current filters.",
                  exportAdminPack
                );
              }}
              disabled={isReadOnly}
            >
              Export admin pack
            </button>
            {orgError && <div className="text-sm" style={{ color: "var(--color-danger)" }}>{orgError}</div>}
            {orgNotice && <div className="text-sm" style={{ color: "var(--color-success)" }}>{orgNotice}</div>}
          </div>
          {orgs.length > 0 && (
            <>
              <div className="mt-3 text-sm opacity-70">
              <div style={{ marginBottom: 10 }}>
                <input
                  className="input"
                  placeholder="Search orgs"
                  value={orgSearch}
                  onChange={(e) => setOrgSearch(e.target.value)}
                />
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                  />
                  Show archived
                </label>
                <select
                  className="input"
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                >
                  <option value="all">All tags</option>
                  {uniqueTags.map((tag) => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setOrgSearch("");
                    setTagFilter("all");
                    setShowArchived(false);
                    clearOrgSelection();
                  }}
                >
                  Clear filters
                </button>
                {filteredOrgs.length > 0 && (
                  <button
                    className="btn btn-ghost"
                    onClick={() => setSelectedOrgIds(new Set(filteredOrgs.map((org) => org.id)))}
                  >
                    Select filtered
                  </button>
                )}
                {selectedOrgIds.size > 0 && (
                  <button className="btn btn-ghost" onClick={clearOrgSelection}>
                    Clear selection
                  </button>
                )}
              </div>
              {selectedOrgIds.size > 0 && (
                <>
                  <div className="nia-list-btn" style={{ cursor: "default", justifyContent: "space-between" }}>
                    <span>{selectedOrgIds.size} selected</span>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        className="btn btn-ghost"
                        onClick={async () => {
                          const ok = window.confirm("Archive selected orgs?");
                          if (!ok) return;
                          const ids = Array.from(selectedOrgIds);
                          await Promise.all(ids.map((id) => updateOrgMeta(user, id, { archived: true })));
                          ids.forEach((id) => logOrgActivity(user, id, "org_archive", { after: { archived: true } }));
                          setOrgs((prev) => prev.map((org) => (selectedOrgIds.has(org.id) ? { ...org, archived: true } : org)));
                          clearOrgSelection();
                        }}
                        disabled={isReadOnly}
                      >
                        Archive
                      </button>
                      <button
                        className="btn btn-ghost"
                        onClick={async () => {
                          const ok = window.confirm("Unarchive selected orgs?");
                          if (!ok) return;
                          const ids = Array.from(selectedOrgIds);
                          await Promise.all(ids.map((id) => updateOrgMeta(user, id, { archived: false })));
                          ids.forEach((id) => logOrgActivity(user, id, "org_unarchive", { after: { archived: false } }));
                          setOrgs((prev) => prev.map((org) => (selectedOrgIds.has(org.id) ? { ...org, archived: false } : org)));
                          clearOrgSelection();
                        }}
                        disabled={isReadOnly}
                      >
                        Unarchive
                      </button>
                      <button
                        className="btn btn-ghost"
                        onClick={() => applyBulkTags("add")}
                        disabled={isReadOnly}
                      >
                        Add tags
                      </button>
                      <button
                        className="btn btn-ghost"
                        onClick={() => applyBulkTags("replace")}
                        disabled={isReadOnly}
                      >
                        Replace tags
                      </button>
                      <button className="btn btn-ghost" onClick={clearOrgSelection}>
                        Clear selection
                      </button>
                    </div>
                  </div>
                  <div className="nia-list-btn" style={{ cursor: "default", gap: 10 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span>Bulk tags</span>
                    </label>
                    <input
                      className="input"
                      placeholder="e.g. pilot, enterprise"
                      value={bulkTagInput}
                      onChange={(e) => setBulkTagInput(e.target.value)}
                    />
                    <span className="text-xs opacity-70">Comma separated</span>
                  </div>
                  <div className="nia-list-btn" style={{ cursor: "default", gap: 8, flexWrap: "wrap" }}>
                    <span className="text-xs opacity-70">Quick tags</span>
    {uniqueQuickTags.map((tag) => (
      <button
        key={tag}
        className="btn btn-ghost"
                        onClick={() => {
                          setBulkTagInput((prev) => {
                            if (!prev) return tag;
                            const tokens = prev.split(",").map((t) => t.trim()).filter(Boolean);
                            if (tokens.includes(tag)) return prev;
                            return `${prev}, ${tag}`;
                          });
                        }}
                      >
                        {tag}
                      </button>
                    ))}
                    <div style={{ display: "flex", gap: 6 }}>
                      <input
                        className="input"
                        placeholder="Add preset"
                        value={quickTagInput}
                        onChange={(e) => setQuickTagInput(e.target.value)}
                      />
                      <button className="btn btn-ghost" onClick={addQuickTag}>
                        Add
                      </button>
                    </div>
                    {uniqueQuickTags.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {uniqueQuickTags.map((tag) => (
                          <button key={`${tag}-remove`} className="btn btn-ghost" onClick={() => removeQuickTag(tag)}>
                            Remove {tag}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="nia-list-btn" style={{ cursor: "default", gap: 10 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span>Bulk seats</span>
                    </label>
                    <input
                      className="input"
                      type="number"
                      min="1"
                      placeholder="e.g. 50"
                      value={bulkSeats}
                      onChange={(e) => setBulkSeats(e.target.value)}
                    />
                    <button
                      className="btn btn-ghost"
                      onClick={async () => {
                        const seats = Number(bulkSeats);
                        if (!Number.isFinite(seats) || seats <= 0) {
                          setOrgError("Enter a valid seat count.");
                          return;
                        }
                        const ok = window.confirm(`Set plan seats to ${seats} for ${selectedOrgIds.size} orgs?`);
                        if (!ok) return;
                        const ids = Array.from(selectedOrgIds);
                        await Promise.all(ids.map((id) => updateOrgMeta(user, id, { planSeats: seats })));
                        ids.forEach((id) => logOrgActivity(user, id, "org_update", { after: { planSeats: seats } }));
                        setOrgNotice(`Updated seats to ${seats} for ${selectedOrgIds.size} orgs`);
                        setBulkSeats("");
                        clearOrgSelection();
                      }}
                      disabled={isReadOnly}
                    >
                      Apply seats
                    </button>
                  </div>
                </>
              )}
              </div>
              {filteredOrgs.map((org) => (
                <div key={org.id} className="nia-list-btn" style={{ cursor: "default", gap: 10, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={selectedOrgIds.has(org.id)}
                      onChange={() => toggleOrgSelection(org.id)}
                    />
                    <span>{org.name} ({org.id})</span>
                  </label>
                  <span className="nia-pill">{org.archived ? "Archived" : "Active"}</span>
                  <span className={`nia-pill pill-${String(org.type || "pilot").toLowerCase()}`}>
                    {(org.type || "pilot").toUpperCase()}
                  </span>
                  {org.tags?.length ? (
                    <span className="nia-pill">{org.tags.join(", ")}</span>
                  ) : null}
                  <button
                    className="btn btn-ghost"
                    onClick={() => setActiveOrgId(org.id)}
                    disabled={isReadOnly}
                  >
                    Switch
                  </button>
                  <button
                    className="btn btn-ghost"
                    onClick={() => copyText(org.id)}
                  >
                    Copy ID
                  </button>
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      confirmExport(
                        "Download org export pack",
                        "This will download a ZIP containing sessions, activity, and audit CSV files for this org.",
                        () => exportOrgPack(org.id)
                      );
                    }}
                    disabled={isReadOnly}
                  >
                    Export org pack
                  </button>
                  <button
                    className="btn btn-ghost"
                    onClick={async () => {
                      const label = org.archived ? "Unarchive org" : "Archive org";
                      const ok = window.confirm(`${label}: ${org.name}?`);
                      if (!ok) return;
                      const updated = await updateOrgMeta(user, org.id, { archived: !org.archived });
                      if (!updated) {
                        setOrgError("Unable to update org. Check Firestore rules for superadmin write access.");
                        return;
                      }
                      applyOrgUpdate(org.id, { archived: !org.archived });
                      setOrgNotice(label);
                      logOrgActivity(user, org.id, org.archived ? "org_unarchive" : "org_archive", {
                        before: { archived: org.archived },
                        after: { archived: !org.archived },
                      });
                      setUndo(label, async () => {
                        const okUndo = await updateOrgMeta(user, org.id, { archived: org.archived });
                        if (okUndo) applyOrgUpdate(org.id, { archived: org.archived });
                      });
                    }}
                    disabled={isReadOnly}
                  >
                      {org.archived ? "Unarchive" : "Archive"}
                    </button>
                    <button
                      className="btn btn-ghost"
                      onClick={async () => {
                        const input = prompt("Tags (comma separated)", (org.tags || []).join(", "));
                        if (input === null) return;
                        const tags = input.split(",").map((t) => t.trim()).filter(Boolean);
                        const updated = await updateOrgMeta(user, org.id, { tags });
                        if (!updated) {
                          setOrgError("Unable to update tags. Check Firestore rules for superadmin write access.");
                          return;
                        }
                        applyOrgUpdate(org.id, { tags });
                        setOrgNotice("Tags updated");
                        logOrgActivity(user, org.id, "org_tags_update", {
                          before: { tags: org.tags || [] },
                          after: { tags },
                        });
                        setUndo("Update tags", async () => {
                          const okUndo = await updateOrgMeta(user, org.id, { tags: org.tags || [] });
                          if (okUndo) applyOrgUpdate(org.id, { tags: org.tags || [] });
                        });
                      }}
                      disabled={isReadOnly}
                    >
                      Edit tags
                    </button>
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setEditingOrgId(org.id);
                      setEditName(org.name || "");
                      setEditLogo(org.logoUrl || "");
                      setEditType(String(org.type || "pilot"));
                      setEditLogoError(false);
                    }}
                    disabled={isReadOnly}
                  >
                    Edit org
                  </button>
                  {editingOrgId === org.id && (
                    <div style={{ display: "grid", gap: 6, marginTop: 8, width: "100%" }}>
                      <input
                        className="input"
                        placeholder="Org name"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                      <input
                        className="input"
                        placeholder="Logo URL"
                        value={editLogo}
                        onChange={(e) => {
                          setEditLogo(e.target.value);
                          setEditLogoError(false);
                        }}
                      />
                      <select
                        className="input"
                        value={editType}
                        onChange={(e) => setEditType(e.target.value)}
                      >
                        <option value="live">Live</option>
                        <option value="pilot">Pilot</option>
                        <option value="demo">Demo</option>
                      </select>
                      <div className="text-xs opacity-70">Square PNG/JPG works best. Leave blank for none.</div>
                      {editLogo && !isValidUrl(editLogo) && (
                        <div className="text-xs" style={{ color: "var(--color-danger)" }}>
                          Please enter a valid http(s) URL.
                        </div>
                      )}
                      {editLogo && isValidUrl(editLogo) && !editLogoError && (
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <img
                            src={editLogo}
                            alt="Org logo preview"
                            style={{ width: 48, height: 48, borderRadius: 12, objectFit: "cover", border: "1px solid var(--ui-border)" }}
                            onError={() => setEditLogoError(true)}
                          />
                          <span className="text-xs opacity-70">Logo preview</span>
                        </div>
                      )}
                      {editLogoError && (
                        <div className="text-xs" style={{ color: "var(--color-danger)" }}>
                          Unable to load this image. Check the URL.
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          className="btn btn-primary"
                          onClick={async () => {
                            if (!editName.trim()) {
                              setOrgError("Org name is required.");
                              return;
                            }
                            if (editLogo && !isValidUrl(editLogo)) {
                              setOrgError("Logo URL must be a valid http(s) link.");
                              return;
                            }
                            const updated = await updateOrgMeta(user, org.id, { name: editName, logoUrl: editLogo, type: editType });
                            if (!updated) {
                              setOrgError("Unable to update org. Check Firestore rules for superadmin write access.");
                              return;
                            }
                            applyOrgUpdate(org.id, { name: editName, logoUrl: editLogo, type: editType });
                            setOrgNotice("Org updated");
                            logOrgActivity(user, org.id, "org_update", {
                              before: { name: org.name, logoUrl: org.logoUrl || "", type: org.type || "pilot" },
                              after: { name: editName, logoUrl: editLogo, type: editType },
                            });
                            setUndo("Update org profile", async () => {
                              const okUndo = await updateOrgMeta(user, org.id, { name: org.name, logoUrl: org.logoUrl || "", type: org.type || "pilot" });
                              if (okUndo) applyOrgUpdate(org.id, { name: org.name, logoUrl: org.logoUrl || "", type: org.type || "pilot" });
                            });
                            setEditingOrgId(null);
                          }}
                          disabled={isReadOnly || !editName.trim() || (editLogo && !isValidUrl(editLogo))}
                        >
                          Save
                        </button>
                        <button className="btn btn-ghost" onClick={() => setEditingOrgId(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </Card>
      )}

      {user?.role === "superadmin" && (
        <Card>
          <h3 className="text-lg font-semibold">Org Switch Audit</h3>
          <p className="text-sm opacity-70 mt-2">
            Recent org switches for the active org.
          </p>
          <div className="mt-3">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <input
                className="input"
                placeholder="Filter by actor email"
                value={auditFilter}
                onChange={(e) => setAuditFilter(e.target.value)}
              />
              <select
                className="input"
                value={auditDays}
                onChange={(e) => setAuditDays(e.target.value)}
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="0">All time</option>
              </select>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  checked={auditAllOrgs}
                  onChange={(e) => setAuditAllOrgs(e.target.checked)}
                />
                All orgs
              </label>
              {auditAllOrgs && (
                <select
                  className="input"
                  value={auditOrgId}
                  onChange={(e) => setAuditOrgId(e.target.value)}
                >
                  <option value="all">All orgs</option>
                  {orgs.map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              )}
              <select
                className="input"
                value={auditRetentionDays}
                onChange={(e) => setAuditRetentionDays(e.target.value)}
              >
                <option value="30">Retention 30 days</option>
                <option value="90">Retention 90 days</option>
                <option value="180">Retention 180 days</option>
                <option value="0">Retention off</option>
              </select>
              <button
                className="btn btn-ghost"
                onClick={() => exportAuditCsv(filteredAudit, auditOrgId || "all")}
              >
                Export CSV
              </button>
            </div>
            {auditRows.length === 0 ? (
              <div className="text-sm opacity-70">No audit events yet.</div>
            ) : (
              <div className="nia-list">
                {filteredAudit.map((row) => (
                  <div key={row.id} className="nia-list-btn" style={{ cursor: "default" }}>
                    <span>{row.actorEmail || row.actorId}</span>
                    <span className="nia-pill">{row.createdAt?.toDate?.().toLocaleString?.() || "Just now"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {user?.role === "superadmin" && (
        <Card>
          <h3 className="text-lg font-semibold">Recent Activity</h3>
          <p className="text-sm opacity-70 mt-2">
            Latest org events (logins, sessions, role changes).
          </p>
          <div className="mt-3">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <input
                className="input"
                placeholder="Search by actor email"
                value={activitySearch}
                onChange={(e) => setActivitySearch(e.target.value)}
              />
              {activitySearch.trim() && (
                <button className="btn btn-ghost" onClick={() => setActivitySearch("")}>
                  Clear
                </button>
              )}
              <select
                className="input"
                value={activityType}
                onChange={(e) => setActivityType(e.target.value)}
              >
                <option value="all">All types</option>
                <option value="login">Login</option>
                <option value="session_created">Session Created</option>
                <option value="role_change">Role Change</option>
                <option value="org_update">Org Update</option>
                <option value="org_tags_update">Org Tags</option>
                <option value="org_archive">Org Archive</option>
                <option value="org_unarchive">Org Unarchive</option>
              </select>
              <select
                className="input"
                value={activityDays}
                onChange={(e) => setActivityDays(e.target.value)}
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="0">All time</option>
              </select>
              <button className="btn btn-ghost" onClick={() => setActivityDays("7")}>7d</button>
              <button className="btn btn-ghost" onClick={() => setActivityDays("30")}>30d</button>
              <button className="btn btn-ghost" onClick={() => setActivityDays("90")}>90d</button>
              <button
                className="btn btn-ghost"
                onClick={() => exportActivityCsv(activityFeed, activeOrgId || "org_default")}
              >
                Export Activity CSV
              </button>
            </div>
            <div className="text-xs opacity-70" style={{ marginBottom: 8 }}>
              Showing {activityFeed.length} of {filteredActivity.length} events
            </div>
            {activityFeed.length === 0 ? (
              <div className="text-sm opacity-70">No recent activity.</div>
            ) : (
              <div className="nia-list">
                {activityFeed.map((row) => {
                  const detail = activityDetail(row);
                  return (
                    <div key={row.id} className="nia-list-btn" style={{ cursor: "default", flexDirection: "column", alignItems: "flex-start" }}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <span>{row.actorEmail || row.actorId}</span>
                        <span className="nia-pill">{row.type || "event"}</span>
                        <span className="nia-pill">{row.createdAt?.toDate?.().toLocaleString?.() || "Just now"}</span>
                      </div>
                      {detail && (
                        <div className="text-xs opacity-70" style={{ marginTop: 4 }}>
                          {detail}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      )}

      {canManageAnnouncements && (
        <Card>
          <h3 className="text-lg font-semibold">Dashboard Announcements</h3>
          <p className="text-sm opacity-70 mt-2">
            Publish short updates shown in Dashboard “News & Releases”.
          </p>
          {editingAnnouncementId ? (
            <div className="text-sm opacity-70 mt-2">
              Editing announcement {editingAnnouncementId}. Saving will update the existing item instead of creating a new one.
            </div>
          ) : null}
          <div className="nia-form" style={{ marginTop: 12 }}>
            <label>Title</label>
            <input
              className="input"
              value={announceTitle}
              onChange={(e) => setAnnounceTitle(e.target.value)}
              placeholder="Nia COACH update title"
              disabled={isReadOnly || publishingAnnouncement}
            />
            <label>Detail</label>
            <textarea
              className="textarea"
              rows={3}
              value={announceDetail}
              onChange={(e) => setAnnounceDetail(e.target.value)}
              placeholder="Short supporting detail"
              disabled={isReadOnly || publishingAnnouncement}
            />
            <label>Tag</label>
            <input
              className="input"
              value={announceTag}
              onChange={(e) => setAnnounceTag(e.target.value)}
              placeholder="Release / Pilot / Coming soon"
              disabled={isReadOnly || publishingAnnouncement}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={announceActive}
                onChange={(e) => setAnnounceActive(e.target.checked)}
                disabled={isReadOnly || publishingAnnouncement}
              />
              Active
            </label>
            <button
              className="btn btn-primary"
              disabled={isReadOnly || publishingAnnouncement || !announceTitle.trim() || !announceDetail.trim()}
              onClick={async () => {
                if (!announceTitle.trim() || !announceDetail.trim()) return;
                setPublishingAnnouncement(true);
                setAnnounceStatus(editingAnnouncementId ? "Saving changes..." : "Publishing...");
                try {
                  const payload = {
                    title: announceTitle.trim(),
                    detail: announceDetail.trim(),
                    tag: announceTag.trim() || "Update",
                    active: Boolean(announceActive),
                    updatedAt: serverTimestamp(),
                  };
                  if (editingAnnouncementId) {
                    await updateDoc(doc(db, "announcements", editingAnnouncementId), payload);
                    setAnnounceStatus("Announcement updated.");
                  } else {
                    await addDoc(collection(db, "announcements"), {
                      ...payload,
                      createdAt: serverTimestamp(),
                    });
                    setAnnounceStatus("Announcement published.");
                  }
                  resetAnnouncementForm();
                  await loadRecentAnnouncements();
                } catch (err) {
                  const code = err?.code ? String(err.code).replace(/^firestore\//, "") : "";
                  const reason = err?.message ? String(err.message) : "";
                  const hint = code || reason ? ` (${code || reason})` : "";
                  setAnnounceStatus(`Unable to save announcement${hint}.`);
                } finally {
                  setPublishingAnnouncement(false);
                }
              }}
            >
              {publishingAnnouncement ? (editingAnnouncementId ? "Saving..." : "Publishing...") : (editingAnnouncementId ? "Save Changes" : "Publish Announcement")}
            </button>
            {editingAnnouncementId ? (
              <button
                className="btn btn-ghost"
                disabled={isReadOnly || publishingAnnouncement}
                onClick={() => {
                  resetAnnouncementForm();
                  setAnnounceStatus("Edit cancelled.");
                }}
              >
                Cancel Edit
              </button>
            ) : null}
            {announceStatus && <div className="text-sm opacity-70">{announceStatus}</div>}
          </div>

          <div className="mt-4">
            <h4 className="text-sm font-semibold" style={{ marginBottom: 8 }}>Recent Announcements</h4>
            {recentAnnouncements.length === 0 ? (
              <div className="text-sm opacity-70">No announcements yet.</div>
            ) : (
              <div className="nia-list">
                {recentAnnouncements.map((item) => (
                  <div key={item.id} className="nia-list-btn" style={{ cursor: "default", flexDirection: "column", alignItems: "flex-start" }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600 }}>{item.title || "Announcement"}</span>
                      <span className="nia-pill">{item.tag || "Update"}</span>
                      <span className="nia-pill">{item.active ? "Active" : "Inactive"}</span>
                    </div>
                    <div className="text-sm opacity-80" style={{ marginTop: 4 }}>{item.detail || ""}</div>
                    <div className="text-xs opacity-60" style={{ marginTop: 4 }}>
                      {item.createdAt?.toDate?.().toLocaleString?.() || "Just now"}
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <button
                        className="btn btn-ghost"
                        disabled={isReadOnly || publishingAnnouncement}
                        onClick={() => beginAnnouncementEdit(item)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      <Card>
        <h3 className="text-lg font-semibold">Data Tools</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            className="btn btn-ghost"
            onClick={async () => {
              const orgId = activeOrgId || "org_default";
              seedOrgDemo(orgId);
              await seedOrgSessionsToFirestore(user, orgId);
              await seedOrgUsers(user, orgId);
              await syncLocalSessionsToFirestore(user, activeOrgId);
              alert("Demo data seeded.");
            }}
            disabled={isReadOnly}
          >
            Seed Demo Data
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => {
              const orgId = activeOrgId || "org_default";
              clearOrgDemo(orgId);
              alert("Demo data cleared.");
            }}
            disabled={isReadOnly}
          >
            Clear Demo Data
          </button>
          <button className="btn btn-ghost" onClick={exportSessions} disabled={isReadOnly}>
            Export Sessions (.json)
          </button>
          {user?.role === "superadmin" && (
            <button
              className="btn btn-ghost"
              onClick={() => exportOrgSessionsCsv(user, activeOrgId || "org_default")}
            >
              Export Org Sessions (.csv)
            </button>
          )}
          {user?.role === "superadmin" && (
            <button
              className="btn btn-ghost"
              onClick={async () => {
                const orgId = activeOrgId || "org_default";
                const ok = window.confirm(
                  `Permanently delete all Firestore sessions for ${orgId}? This cannot be undone.`
                );
                if (!ok) return;
                await pruneOrgSessions(user, orgId, 0);
                alert("Firestore sessions purged.");
              }}
            >
              Purge Org Sessions (Firestore)
            </button>
          )}
        </div>
        <p className="text-sm opacity-70 mt-2">
          Most actions here are safe for demo; Firestore purge deletes data permanently.
        </p>
      </Card>

      {user?.role === "superadmin" && (
        <Card>
          <h3 className="text-lg font-semibold">Session Export Filters</h3>
          <div className="nia-form">
            <label>Role</label>
            <select value={sessionExportRole} onChange={(e) => setSessionExportRole(e.target.value)}>
              <option value="all">All roles</option>
              <option value="member">Member</option>
              <option value="coach">Coach</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Superadmin</option>
            </select>
            <label>Time range</label>
            <select value={sessionExportDays} onChange={(e) => setSessionExportDays(e.target.value)}>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="0">All time</option>
            </select>
            <label>User search</label>
            <input
              className="input"
              placeholder="Name or email"
              value={sessionExportUser}
              onChange={(e) => setSessionExportUser(e.target.value)}
            />
            <label>Tag (optional)</label>
            <input
              className="input"
              placeholder="e.g. onboarding"
              value={sessionExportTag}
              onChange={(e) => setSessionExportTag(e.target.value)}
            />
            <div className="text-xs opacity-70">
              Matches sessions where the tag exists in the session `tags` array.
            </div>
            <button
              className="btn btn-ghost"
              onClick={() =>
                exportSessionsCsvFiltered(
                  user,
                  activeOrgId || "org_default",
                  { role: sessionExportRole, days: sessionExportDays, userSearch: sessionExportUser, tag: sessionExportTag },
                  `nia-sessions-${activeOrgId || "org_default"}.csv`
                )
              }
            >
              Export Filtered Sessions CSV
            </button>
            <div className="text-xs opacity-70">
              Tip: export before pruning if you need an archive.
            </div>
          </div>
        </Card>
      )}

      {user?.role === "superadmin" && (
        <Card>
          <h3 className="text-lg font-semibold">Session Cleanup</h3>
          <div className="nia-form">
            <label>Delete sessions older than (days)</label>
            <input
              className="input"
              type="number"
              min="30"
              value={sessionPruneDays}
              onChange={(e) => setSessionPruneDays(e.target.value)}
            />
            <button
              className="btn btn-ghost"
              onClick={async () => {
                const count = await countOrgSessionsOlderThan(user, activeOrgId || "org_default", sessionPruneDays);
                setPrunePreview(count);
              }}
            >
              Preview count
            </button>
            {prunePreview !== null && (
              <div className="text-sm opacity-70">
                {prunePreview} sessions will be deleted.
              </div>
            )}
            {prunePreview === null && (
              <div className="text-xs opacity-70">
                Tip: run “Preview count” before pruning.
              </div>
            )}
            <button
              className="btn btn-ghost"
              onClick={() => {
                if (!sessionPruneDays) return;
                const countText = prunePreview !== null ? ` (${prunePreview} sessions)` : "";
                const ok = window.confirm(`This will permanently delete old sessions${countText}. Continue?`);
                if (!ok) return;
                pruneOrgSessions(user, activeOrgId || "org_default", sessionPruneDays);
                setUndo("Prune sessions", () => {});
              }}
            >
              Prune Sessions
            </button>
            <div className="text-xs opacity-70">
              Pruning is irreversible and impacts all users in the org.
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
